<script lang="ts">
  import { onMount } from 'svelte';
  import { problemListStore } from '@entities/problem/store.svelte';
  import { sessionStore } from '@features/auth/session/model/store.svelte';
  import ProblemTable from '@widgets/problem-table/ProblemTable.svelte';
  import ProblemCreateModal from '@features/problem-create/ProblemCreateModal.svelte';
  import ProblemCategorySelect from '@shared/ui/ProblemCategorySelect.svelte';
  import ProblemThemeSelect from '@shared/ui/ProblemThemeSelect.svelte';
  import Pagination from '@shared/ui/Pagination.svelte';
  import { navigate } from '@shared/router/navigation';
  import { canEdit } from '@shared/types/session';
  import type { Problem, ProblemListQuery } from '@shared/types/problem';

  interface FilterValues {
    q: string;
    category: string;
    theme: string;
  }

  let filterValues = $state<FilterValues>({ q: '', category: '', theme: '' });

  let query = $state<ProblemListQuery>({
    sort_by: 'updated_at',
    sort_dir: 'desc',
    page: 1,
    page_size: 200,
  });

  let createOpen = $state(false);

  let allowEdit = $derived(
    sessionStore.user ? canEdit(sessionStore.user.role) : false,
  );

  async function refresh(nextQuery: ProblemListQuery = query) {
    await problemListStore.refresh(nextQuery);
  }

  function buildQuery(
    filters: FilterValues,
    base: ProblemListQuery,
  ): ProblemListQuery {
    const next: ProblemListQuery = {
      sort_by: base.sort_by,
      sort_dir: base.sort_dir,
      page: 1,
      page_size: base.page_size,
    };
    if (filters.q.trim() !== '') next.q = filters.q.trim();
    if (filters.category.trim() !== '') next.category = filters.category.trim();
    if (filters.theme.trim() !== '') next.theme = filters.theme.trim();
    return next;
  }

  function handleSearch(ev: SubmitEvent) {
    ev.preventDefault();
    const nextQuery = buildQuery(filterValues, query);
    query = nextQuery;
    void refresh(nextQuery);
  }

  function handleClear(ev: MouseEvent) {
    ev.preventDefault();
    filterValues = { q: '', category: '', theme: '' };
    const nextQuery: ProblemListQuery = {
      sort_by: query.sort_by,
      sort_dir: query.sort_dir,
      page: 1,
      page_size: query.page_size,
    };
    query = nextQuery;
    void refresh(nextQuery);
  }

  function applyFilters(nextFilters: FilterValues) {
    const nextQuery = buildQuery(nextFilters, query);
    filterValues = nextFilters;
    query = nextQuery;
    void refresh(nextQuery);
  }

  function handleCategoryChange(category: string) {
    applyFilters({ ...filterValues, category, theme: '' });
  }

  function handleThemeChange(theme: string) {
    applyFilters({ ...filterValues, theme });
  }

  function toggleSort(field: NonNullable<ProblemListQuery['sort_by']>) {
    let nextQuery: ProblemListQuery;
    if (query.sort_by === field) {
      nextQuery = {
        ...query,
        sort_dir: query.sort_dir === 'asc' ? 'desc' : 'asc',
        page: 1,
      };
    } else {
      nextQuery = { ...query, sort_by: field, sort_dir: 'asc', page: 1 };
    }
    query = nextQuery;
    void refresh(nextQuery);
  }

  function changePage(page: number) {
    const nextQuery = { ...query, page };
    query = nextQuery;
    void refresh(nextQuery);
  }

  function handleCreated(problem: Problem) {
    createOpen = false;
    navigate(`/master/problems/${problem.id}`);
  }

  onMount(() => {
    void refresh();
  });

  let problems = $derived(problemListStore.data?.items ?? []);
</script>

<header class="settings-header">
  <div>
    <h1>課題</h1>
  </div>
  <button
    type="button"
    class="btn btn-primary"
    disabled={!allowEdit}
    title={allowEdit ? '' : '権限がありません'}
    onclick={() => (createOpen = true)}
  >
    + 課題を追加
  </button>
</header>

{#if problemListStore.error}
  <div class="error">{problemListStore.error}</div>
{:else}
  <div class="panel">
    <form
      class="problem-filter-form"
      onsubmit={handleSearch}
    >
      <div class="filter-control filter-category">
        <ProblemCategorySelect
          value={filterValues.category}
          onChange={handleCategoryChange}
          allowAdd={allowEdit}
          placeholderLabel="大カテゴリ (すべて)"
        />
      </div>
      <div class="filter-control filter-theme">
        <ProblemThemeSelect
          category={filterValues.category}
          value={filterValues.theme}
          onChange={handleThemeChange}
          allowAdd={allowEdit}
          placeholderLabel="中テーマ (すべて)"
        />
      </div>
      <input
        type="text"
        name="q"
        class="problem-search-input"
        placeholder="小課題名で検索"
        bind:value={filterValues.q}
      />
      <button type="submit" class="btn btn-primary">検索</button>
      <button type="button" class="btn" onclick={handleClear}>クリア</button>
    </form>
  </div>
  <div class="panel">
    {#if problemListStore.loading && problemListStore.data === null}
      <div class="empty">読み込み中…</div>
    {:else if problems.length === 0}
      <div class="empty">該当する課題がありません</div>
    {:else}
      <div class="table-scroll">
        <ProblemTable
          {problems}
          sortBy={query.sort_by ?? 'updated_at'}
          sortDir={query.sort_dir ?? 'desc'}
          onSort={toggleSort}
        />
      </div>
    {/if}
  </div>
  {#if problemListStore.data}
    <div class="pagination-wrap">
      <Pagination
        total={problemListStore.data.total}
        page={problemListStore.data.page}
        pageSize={problemListStore.data.page_size}
        onChange={changePage}
      />
    </div>
  {/if}
{/if}

<ProblemCreateModal
  open={createOpen}
  onClose={() => (createOpen = false)}
  onCreated={handleCreated}
/>

<style>
  .settings-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 16px;
    margin-bottom: 16px;
  }
  .settings-header h1 {
    margin: 0;
    font-size: 24px;
  }
  .problem-filter-form {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 8px;
  }
  .filter-control {
    min-width: 0;
  }
  .filter-category {
    flex: 0 1 300px;
  }
  .filter-theme {
    flex: 0 1 220px;
  }
  .filter-control :global(select) {
    width: 100%;
    min-width: 0;
  }
  .problem-search-input {
    flex: 0 1 260px;
    min-width: 220px;
  }
  .problem-filter-form .btn {
    flex: 0 0 auto;
  }
  .pagination-wrap {
    display: flex;
    justify-content: flex-end;
  }
  @media (max-width: 640px) {
    .filter-category,
    .filter-theme,
    .problem-search-input {
      flex: 1 1 100%;
      min-width: 0;
      width: 100%;
    }
    .problem-filter-form .btn {
      flex: 1 1 calc(50% - 4px);
    }
  }
</style>
